import React, { useRef, useState } from 'react';
import { Download, Upload, Save, X } from 'lucide-react';
import { Block, TradingPlatform, AssetClass } from '../../types';
import { exportStrategy, importStrategy } from '../../services/strategyService';

interface ImportExportButtonsProps {
  blocks: Block[];
  onImport: (blocks: Block[]) => void;
}

const ImportExportButtons: React.FC<ImportExportButtonsProps> = ({ blocks, onImport }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportMetadata, setExportMetadata] = useState({
    name: '',
    description: '',
    platform: TradingPlatform.MT5,
    assetClass: AssetClass.FOREX,
    version: '1.0.0',
    tags: '',
  });
  const [error, setError] = useState<string | null>(null);

  const handleExport = () => {
    try {
      const jsonStr = exportStrategy(blocks, {
        ...exportMetadata,
        tags: exportMetadata.tags.split(',').map(tag => tag.trim()).filter(Boolean),
      });
      
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportMetadata.name || 'strategy'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setShowExportDialog(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to export strategy');
    }
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const jsonStr = e.target?.result as string;
        const { blocks: importedBlocks, metadata } = importStrategy(jsonStr);
        onImport(importedBlocks);
        
        // Update export metadata with imported values
        setExportMetadata({
          name: metadata.name,
          description: metadata.description,
          platform: metadata.platform,
          assetClass: metadata.assetClass,
          version: metadata.version,
          tags: metadata.tags.join(', '),
        });
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Failed to import strategy');
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
    };

    reader.readAsText(file);

    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleImport}
        className="hidden"
      />
      
      <button 
        onClick={() => fileInputRef.current?.click()}
        className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
      >
        <Upload className="w-4 h-4 mr-1.5" />
        Import
      </button>
      
      <button
        onClick={() => setShowExportDialog(true)}
        className="flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 transition-colors"
      >
        <Download className="w-4 h-4 mr-1.5" />
        Export
      </button>

      {/* Export Dialog */}
      {showExportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium text-gray-800">Export Strategy</h3>
              <button
                onClick={() => setShowExportDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Strategy Name
                  </label>
                  <input
                    type="text"
                    value={exportMetadata.name}
                    onChange={(e) => setExportMetadata({ ...exportMetadata, name: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="My Trading Strategy"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={exportMetadata.description}
                    onChange={(e) => setExportMetadata({ ...exportMetadata, description: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Describe your strategy..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Platform
                    </label>
                    <select
                      value={exportMetadata.platform}
                      onChange={(e) => setExportMetadata({ 
                        ...exportMetadata, 
                        platform: e.target.value as TradingPlatform 
                      })}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.values(TradingPlatform).map((platform) => (
                        <option key={platform} value={platform}>{platform}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Class
                    </label>
                    <select
                      value={exportMetadata.assetClass}
                      onChange={(e) => setExportMetadata({ 
                        ...exportMetadata, 
                        assetClass: e.target.value as AssetClass 
                      })}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Object.values(AssetClass).map((assetClass) => (
                        <option key={assetClass} value={assetClass}>{assetClass}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Version
                  </label>
                  <input
                    type="text"
                    value={exportMetadata.version}
                    onChange={(e) => setExportMetadata({ ...exportMetadata, version: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1.0.0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={exportMetadata.tags}
                    onChange={(e) => setExportMetadata({ ...exportMetadata, tags: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="trend-following, forex, scalping"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Separate tags with commas
                  </p>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowExportDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  Export Strategy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImportExportButtons;